// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance from '../../services/axiosInstance';
import { apiCall } from '../utils/utils';

import { fetchCompanyScenes } from '../scenes/scenesActions';

// get companies
export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get('company/all/'), rejectWithValue)
);
// get company by Id

export const getCompanyById = createAsyncThunk(
  'company/getCompanyById',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`company/${companyId}/`), rejectWithValue)
);
// get all company members
export const fetchCompanyMembers = createAsyncThunk(
  'company/companyMembers',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`company/member/all/${companyId}/`), rejectWithValue)
);

// get company/member by Id
export const fetchCompanyMember = createAsyncThunk(
  'company/companyMember',
  async (companyMemberId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`company/member/${companyMemberId}/`), rejectWithValue)
);

// add user to company
export const addUserToCompany = createAsyncThunk(
  'company/addUserToCompany',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post('company/add/user/', data), rejectWithValue)
);

// leave company
export const leaveCompany = createAsyncThunk(
  'company/leaveCompany',
  async (companyUuid, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`company/${companyUuid}/leave/`), rejectWithValue)
);

// create company
export const createCompany = createAsyncThunk(
  'company/createCompany',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`company/create/`, data), rejectWithValue)
);

// update company
export const updateCompany = createAsyncThunk(
  'company/updateCompany',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`company/update/`, data), rejectWithValue)
);

// update company owner
export const updateCompanyOwner = createAsyncThunk(
  'company/updateCompanyOwner',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`company/update/owner/`, data), rejectWithValue)
);

// delete user from company
export const removeCompanyUser = createAsyncThunk(
  'company/removeCompanyUser',
  async ({ companyId, userId }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`company/${companyId}/remove/${userId}/`), rejectWithValue)
);

// update company member role
export const updateCompanyMemberRole = createAsyncThunk(
  'company/updateMemberRole',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`company/member/update/`, data), rejectWithValue)
);

// get company invites
export const fetchCompanyInvites = createAsyncThunk(
  'company/companyInvites',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`company/${companyId}/invites/`), rejectWithValue)
);

//  create company member invite
export const createCompanyInvitation = createAsyncThunk(
  'company/createCompanyInvitation',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`company/member/invite/`, data), rejectWithValue)
);

// revoke company member invite
export const revokeCompanyInvitation = createAsyncThunk(
  'company/revokeCompanyInvitation',
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiCall(
        () => axiosInstance.post(`company/member/revoke-invite/`, data),
        rejectWithValue
      );
      // if response has payload, it's an error, return it
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'payload')) {
        return res;
      }

      // success (204 -> res is null), return data for reducer to update state
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || 'Error revoking invitation');
    }
  }
);
// COMPANY STUDIOS
// create studio
export const createStudio = createAsyncThunk(
  'company/createStudio',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`studio/`, data), rejectWithValue)
);

//  update studio
export const updateStudio = createAsyncThunk(
  'company/updateStudio',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`studio/update/`, data), rejectWithValue)
);

// Delete studio
export const deleteStudio = createAsyncThunk(
  'company/deleteStudio',
  async (studioUuid, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`studio/delete/${studioUuid}`), rejectWithValue)
);

// COMPANY PROVIDERS
// get company providers
export const fetchCompanyProviders = createAsyncThunk(
  'company/companyProviders',
  async (companyUuid, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`provider/company-providers/${companyUuid}`), rejectWithValue)
);

// create Provider
export const createProvider = createAsyncThunk(
  'company/createProvider',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`provider/`, data), rejectWithValue)
);

// update provider
export const updateProvider = createAsyncThunk(
  'company/updateProvider',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`provider/update/`, data), rejectWithValue)
);

// delete provider
export const deleteProvider = createAsyncThunk(
  'company/deleteProvider',
  async (providerId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`provider/delete/${providerId}/`), rejectWithValue)
);
// refresh provider
export const refreshProvider = createAsyncThunk(
  'company/refreshProvider',
  async (id, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`provider/refresh-provider/${id}`), rejectWithValue)
);

// get frame.io authorization url
export const authorizeFrameioProvider = createAsyncThunk(
  'company/authorizeFrameioProvider',
  async (providerId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`provider/frame-io/${providerId}/authorize`), rejectWithValue)
);

// refresh all providers
export const refreshAllProvidersAndFetchScenes = createAsyncThunk(
  'company/refreshAllAndFetchScenes',
  async (providers, { dispatch }) => {
    if (!providers?.length) {
      return;
    }

    const companyUuid = providers[0]?.company_uuid;
    // Dispatch refreshProvider for each provider
    await Promise.all(providers.map(provider => dispatch(refreshProvider(provider.mosys_id))));
    // Once all providers are refreshed, fetch scenes
    if (!companyUuid) {
      return;
    }
    await dispatch(fetchCompanyScenes(companyUuid));
  }
);

// COMPANY KEYWORDS
// create keyword
export const createKeyword = createAsyncThunk(
  'company/createKeyword',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`keyword/create/`, data), rejectWithValue)
);

// delete keywords
export const deleteKeyword = createAsyncThunk(
  'company/deleteKeyword',
  async (keywordId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`keyword/delete/${keywordId}`), rejectWithValue)
);

// company equipment
// fetch company equipment list
export const fetchCompanyEquipment = createAsyncThunk(
  'company/fetchCompanyEquipment',
  async (companyUuid, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`company_equipment/${companyUuid}/all/`), rejectWithValue)
);

// create company equipment
export const createCompanyEquipment = createAsyncThunk(
  'company/createCompanyEquipment',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`company_equipment/`, data), rejectWithValue)
);

// update company equipment
export const updateCompanyEquipment = createAsyncThunk(
  'company/updateCompanyEquipment',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`company_equipment/update/`, data), rejectWithValue)
);

// get company equipment types
export const fetchCompanyEquipmentTypes = createAsyncThunk(
  'equipments/fetchCompanyEquipmentTypes',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`equipment_type/company/${companyId}/`), rejectWithValue)
);
